import type { AuthEventEntry } from "@/common/model/AdminUserDetails";
import {
    formatDateTime,
    formatRelativeTime,
    countryFlagEmoji,
    eventTypeLabel,
    eventTypeBadgeClasses,
    methodLabel,
} from "@/utils/authActivityUtils";

interface LoginHistoryTabProps {
    loginHistory?: AuthEventEntry[];
    /** True when the backend auth-event read FAILED (vs. genuinely empty). */
    unavailable?: boolean;
}

// Small provider/method glyph. Google & Apple get their marks; everything else a generic key icon.
const MethodIcon: React.FC<{ method?: string }> = ({ method }) => {
    if (method === "google") {
        return (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06L5.84 9.9c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
        );
    }
    if (method === "apple") {
        return (
            <svg className="w-5 h-5 text-gray-900" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
        );
    }
    // Email code / password / admin / watch → generic key icon.
    return (
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
        </svg>
    );
};

const CountryCell: React.FC<{ country?: string }> = ({ country }) => {
    if (!country) return <span className="text-gray-400">—</span>;
    const flag = countryFlagEmoji(country);
    return (
        <span className="inline-flex items-center gap-1">
            {flag && <span className="text-base leading-none">{flag}</span>}
            <span>{country.toUpperCase()}</span>
        </span>
    );
};

export const LoginHistoryTab: React.FC<LoginHistoryTabProps> = ({ loginHistory, unavailable }) => {
    if (unavailable) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-sm text-amber-600 italic">
                    Could not load login history (data read failed). Try refreshing this user.
                </p>
            </div>
        );
    }

    if (!loginHistory || loginHistory.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-sm text-gray-500 italic">No login history recorded yet</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">Login History</h3>
                <p className="text-xs text-gray-500">
                    {loginHistory.length} most recent event{loginHistory.length === 1 ? "" : "s"}
                </p>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Event</th>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Method</th>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Device</th>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">IP</th>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Country</th>
                            <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">When</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {loginHistory.map((event, index) => (
                            <tr key={`${event.createdAt ?? index}-${index}`} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventTypeBadgeClasses(
                                            event.eventType
                                        )}`}
                                    >
                                        {eventTypeLabel(event.eventType)}
                                    </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                        <MethodIcon method={event.method} />
                                        <span className="text-gray-900">{methodLabel(event.method)}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-gray-700">{event.deviceInfo || "—"}</td>
                                <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-gray-700">
                                    {event.ip || "—"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                                    <CountryCell country={event.country} />
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-gray-700" title={formatDateTime(event.createdAt)}>
                                    {formatRelativeTime(event.createdAt)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-gray-500 italic">
                Shows the {loginHistory.length} most recent auth events. "Login failed" is only recorded for attempts
                that matched an existing account. IP &amp; country are captured from App Engine (country is unavailable
                for events recorded before this was added).
            </p>
        </div>
    );
};
